<template>
  <transition is="tr" enter-active-class="animated fadeIn faster" leave-active-class="animated fadeOut faster" mode="out-in"> 
    <td colspan="15" class="is-clipped" v-if="!loading">
      <div class="is-padding-bottom" v-if="lot">
        <div class="columns is-gapless">
          <div class="column is-narrow w280p">
            <lot-plan :displayFromRow="true" :lot="lot"></lot-plan>
          </div>
          <div class="column columns is-gapless is-multiline">
            <div class="column is-12 is-padding-right is-padding-left is-padding-top-xsmall is-padding-bottom-xsmall">
              <div class="is-uppercase">
                <b>Lot {{lot.ref}} : </b>
                <span class="has-text-grey">Dispositif(s) : </span>
                <span v-if="taxDispositifs">{{taxDispositifs}}</span>
                <span v-else>NC</span>

                <span class="has-text-grey">/ {{actabilityTerm}} : </span>
                <span v-if="lot && lot.date_actabilite">{{lot.date_actabilite | trimestre}}</span>
                <span v-else>NC</span>

                <span class="has-text-grey">/ Livraison : </span>
                <span v-if="lot && lot.date_livraison">{{lot.date_livraison | trimestre}}</span>
                <span v-else >NC</span>
                <span class="has-text-grey" v-if="allotment && isAdmin">/ Allotement :</span>
                <span v-if="allotment && isAdmin"> {{allotment}} société(s)</span>
                <span class="icon has-text-grey tooltip is-tooltip-top"
                      data-tooltip="Flux promoteur" v-if="isDistributeurMode && lot.from_flux && !isExternal">
                  <i class="fas fa-rss"></i>
                </span>
              </div>
              <div class="tags is-margin-top-xsmall">
                <span v-if="program && isAdmin" class="tag" :class="statusColor">{{program.status | workflowLabel}}</span>
              </div>
            </div>
            <div class="column is-padding-top-xsmall is-padding-bottom-xsmall is-padding-right is-padding-left is-border-right is-border-dotted">
              <div class="title is-5 is-margin-bottom-xsmall has-text-grey">Annexe / Parking</div>
              <div class="has-text-weight-medium is-size-6">
                <div v-for="(items, type) in annexes" :key="'annex-'+type" v-if="annexes && !!Object.values(annexes).length"
                      class="has-text-grey has-text-weight-medium">
                  <span class="has-text-black" v-if="items.count > 1">{{items.count}}</span>
                  <span class="has-text-black">{{ type | annexTypeLabel | cap | pluralize(items.count > 1) }}</span>
                  <span class="has-text-black" v-if="(items.infos && !!items.infos.length) || items.surface"> : </span>
                  <span v-for="i in items.count" :key="i">
                    <span v-if="items.surface && items.surface[i - 1]">{{items.surface[i - 1]}} m²</span>
                    <span class="has-text-weight-light" v-if="items.infos && !!items.infos[i - 1]">({{items.infos[i - 1]}})</span>
                    <span v-if="items.count > 1 && i !== items.count">/ </span>
                  </span>
                </div>
                <span class="has-text-black" v-if="parkings && !!parkings.totalCount">
                  {{parkings.totalCount}} Parking(s) :
                </span>
                <span v-if="parkings && !!parkings.totalCount" class="has-text-grey has-text-weight-medium" v-for="i in parkings.totalCount" :key="i">
                  <span v-if="parkings.totalCount > 1">{{ parkings.count[i - 1] }}</span>
                  <span>{{ parkings.type[i - 1] | cap }}</span>
                  <span class="has-text-weight-light" v-if="parkings.infos && !!parkings.infos[i - 1]">({{ parkings.infos[i - 1] }})</span>
                  <span v-if="parkings.totalCount > 1 && i !== parkings.totalCount && i <= parkings.count.length - 1">/ </span>
                </span>
                <i v-if="(!annexes || !Object.values(annexes).length) && (!parkings || !parkings.totalCount)">NC</i>
              </div>
            </div>
            <div class="column is-padding-top-xsmall is-padding-bottom-xsmall is-padding-left is-border-dotted">
              <div class="title is-5 is-margin-bottom-xsmall has-text-grey">Prix / Loyer</div>
              <div class="has-text-weight-medium is-size-6">
                <span class="has-text-black">Prix de vente HT :
                  <span class="has-text-grey">
                    <span v-if="lot.sale_price_ht">{{lot.sale_price_ht | prettyPrice}} {{$currency}}</span>
                    <i v-else>NC</i>
                  </span>
                </span>
              </div>
              <div class="has-text-weight-medium is-size-6">
                <span class="has-text-black">Prix de vente TTC :
                  <span class="has-text-grey">
                    <span v-if="lot.sale_price">{{lot.sale_price | prettyPrice}} {{$currency}}</span>
                    <i v-else>NC</i>
                  </span>
                </span>
              </div>


              <div class="has-text-weight-medium is-size-6">
                <span class="has-text-black">Loyer :
                  <span class="has-text-grey">
                    <span v-if="lot.rent">{{lot.rent | prettyPrice}} {{$currency}}</span>
                    <i v-else>NC</i>
                  </span>
                </span>
              </div>
              <div class="has-text-weight-medium is-size-6">
                <span class="has-text-black">Rentabilité :
                  <span class="has-text-grey">
                    <span v-if="lot.profitability">{{lot.profitability|prettyPrice}} %</span>
                    <i v-else>NC</i>
                  </span>
                </span>
              </div>
            </div>
            <div class="column is-padding-top-xsmall is-padding-bottom-xsmall is-padding-right is-padding-left" :class="{'is-border-left' : lot.custom_prices.length !== 0}">
              <template v-if="lot.custom_prices.length !== 0">
                <div class="title is-5 is-margin-bottom-xsmall has-text-grey">Package</div>
                <div class="has-text-weight-medium is-size-6"
                      v-if="lot.custom_prices && lot.custom_prices.length"
                      v-for="(customPrice, i) in lot.custom_prices" :key="i">
                  <span class="has-text-black">{{customPrice.name}} TTC :
                    <span class="has-text-grey">
                      <span v-if="customPrice.sale_price">{{customPrice.sale_price | prettyPrice}} {{$currency}}</span>
                      <span v-else><i>NC</i></span>
                    </span>
                  </span>
                </div>
              </template>
            </div>
            <div class="column is-12 is-margin-top-small is-padding-top-xsmall is-padding-bottom-xsmall is-padding-left">
              <div class="buttons">
                <a v-if="!wishlisted" @click.prevent.stop="addToWishlist" @mouseover="overFav = true" @mouseleave="overFav = false"
                  class="button is-small is-square is-border is-lighter is-transform-none is-lGradient">
                  <span class="icon is-small"><i class="fa-star" :class="overFav ? 'fas' : 'fal'"></i></span>
                </a>
                <a v-else @click.prevent.stop="deleteFromWishlist"
                  class="button is-small is-square is-border is-lighter is-transform-none is-lGradient">
                  <span class="icon is-small"><i class="fas fa-star"></i></span>
                </a>
                <router-link :to="{name: 'ProgramVue', params: {id: lot.program}, query: {returnId: lotPk}}"
                              v-if="$route.name !== 'ProgramVue'"
                              class="button is-small is-border is-lighter is-transform-none is-lGradient">
                  <span>Fiche programme</span>
                </router-link>
                <router-link :to="{name: lot.nature === 'parking' ? 'ParkingVue' : 'LotVue', params: {id: lot.pk}, query: {returnId: lotPk}}"
                              v-if="$route.params.id !== lotPk"
                              class="button is-small is-primary is-transform-none is-lGradient">
                  <span>Fiche détaillée du lot</span>
                </router-link>          
              </div>
            </div>          
          </div>
        </div>
      </div>
    </td>
  </transition>
</template>

<script>
  import LotMixins from './LotMixins'

  export default {
    name: 'LotRowDetail',
    mixins: [LotMixins],
    data () {
      return {
        overFav: false
      }
    },
    props: {
      lotRowPk: String
    },
    computed: {
      lotPk () {
        return this.lotRowPk
      },
      actabilityTerm () {
        if (!this.configs) return
        let term = {
          FRA: 'Actabilité',
          MAR: 'Titre foncier'
        }
        return term[this.configs.COUNTRY]
      }
    },
    methods: {
      getLot () {
        this.loading = true
        this.$emit('loading', true)
        this.service.getOne(this.lotPk).then(res => {
          this.loading = false
          this.$emit('loading', false)
          this.lot = res.data
          if (this.isAdmin) {
            this.getAllotment()
          }
        }).catch(() => {
          this.loading = false
          this.$emit('loading', false)
          this.$router.replace({name: 'NotFound'})
        })
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/lot/LotRowDetail.vue